import React, { Fragment, useState } from 'react';

import { Alert, App, Button, Flex, Form, FormProps, Input, Typography, UploadFile } from 'antd';

import { UnexpectedTypeError } from 'lib/errors/UnexpectedTypeError';
import { makeCpf } from 'lib/helpers/Domain.helper';
import { UploadField } from 'lib/UploadField';
import { LoadedInvite } from 'pages/invite/invite.type';
import { createGuest as createGuestService, updateGuest as updateGuestService } from 'services/Guest.service';
import { signUpInvite } from 'services/Invite.service';
import { Domain } from 'types/Domain.type';
import { Guest } from 'types/Guest.type';

import { NextStep, PreviousStep } from './useSteps';

type Props = {
    invite: LoadedInvite,
    guest: Guest.Model | null,
    setGuest: Utility.SetState<Props['guest']>,
    rawCpf: Domain.RawCpf | null,
    nextStep: NextStep,
    previousStep: PreviousStep,
};

type Values = {
    name: Guest.Model['name'],
    picture: [UploadFile],
};

export function SignUpStep({ invite, guest, setGuest, rawCpf, nextStep, previousStep }: Props) {
    if (rawCpf === null)
        throw new UnexpectedTypeError('rawCpf', 'null', 'Domain.RawCpf');

    const [isSigningUp, setIsSigningUp] = useState(false);
    const [isCreatingOrUpdatingGuest, setIsCreatingOrUpdatingGuest] = useState(false);

    const [form] = Form.useForm<Values>();

    const app = App.useApp();

    const isKnowGuest = guest !== null;

    const createOrUpdateGuest = async ({ name, ...restValues }: Values): Promise<Guest.Model | null> => {
        const picture = restValues.picture[0];

        const parsedPicture = picture?.response || picture?.url;

        if (typeof parsedPicture !== 'string')
            throw new UnexpectedTypeError('parsedPicture', typeof parsedPicture, 'string');

        setIsCreatingOrUpdatingGuest(true);

        const response = isKnowGuest
            ? await updateGuestService(guest.cpf, { name, picture: parsedPicture })
            : await createGuestService({ name, cpf: rawCpf, picture: parsedPicture });

        setIsCreatingOrUpdatingGuest(false);

        app.notification.open(response);

        const localGuest = response.guest ?? null;

        return localGuest;
    };

    const onFinish: FormProps<Values>['onFinish'] = async (values) => {
        const localGuest = form.isFieldsTouched()
            ? await createOrUpdateGuest(values)
            : guest;

        // Try again
        if (!localGuest)
            return;

        setIsSigningUp(true);

        const response = await signUpInvite(invite.uuid,  { guest_id: localGuest.id });

        setIsSigningUp(false);

        app.notification.open(response);

        // Try again, but with the recently created guest to improves UX
        if (!('invite_guest' in response)) {
            setGuest(localGuest);

            return;
        }

        nextStep();
    };

    const message = !isKnowGuest ? (
        <Fragment>
            Não foi possível encontrar uma correspondência no sistema para o CPF
            {' '}
            <span style={{ fontWeight: 'var(--ant-font-weight-strong)' }}>{makeCpf(rawCpf)}</span>.
            {' '}
            Por favor, preencha os campos abaixo para realizar o seu cadastro.
        </Fragment>
    ) : (
        <Fragment>
            O visitante com o CPF
            {' '}
            <span style={{ fontWeight: 'var(--ant-font-weight-strong)' }}>{makeCpf(rawCpf)}</span>
            {' '}
            já está cadastrado no sistema. Por favor, confirme os dados.
        </Fragment>
    );

    const initialValues: Partial<Values> | undefined = isKnowGuest ? {
        name: guest.name,
        picture: guest.picture !== null ? [{ 
            uid: guest.id.toString(),
            name: '', 
            url: guest.picture, 
            status: 'done' 
        }] : undefined,
    } : undefined;

    return (
        <Flex vertical gap="middle">
            <Typography.Paragraph style={{ margin: 0, fontWeight: 600 }}>
                {isKnowGuest ? 'Confirmação de dados cadastrais' : 'Cadastro de novo visitante' }
            </Typography.Paragraph>

            <Alert
                message={message}
                type={isKnowGuest ? 'info' : 'warning'}
                showIcon
            />

            <Form<Values>                  
                form={form}
                onFinish={onFinish}
                initialValues={initialValues}
                name="signUpStep"
                layout="vertical"
            >
                <Form.Item
                    name="name"
                    label="Nome completo"
                    rules={[{ required: true, message: 'Por favor, digite o seu nome completo.' }]}
                >
                    <Input 
                        maxLength={256}
                        placeholder="Digite seu nome completo" 
                    />
                </Form.Item>

                <UploadField
                    name="picture"
                    type="picture-card"
                    label="Foto para reconhecimento facial"
                    extra="A foto deve mostrar claramente seu rosto sem óculos escuros ou chapéu."
                    maxCount={1}
                    multiple={false}
                    rules={[{ required: true, message: 'Por favor, carregue uma foto sua.' }]}
                    accept="image/png, image/jpeg"
                />

                <Flex justify="flex-end" gap="middle">
                    <Button 
                        type="default"
                        htmlType="button"
                        onClick={() => previousStep()}
                    >
                        Voltar
                    </Button>

                    <Button 
                        type="primary" 
                        htmlType="submit"
                        loading={isCreatingOrUpdatingGuest || isSigningUp}
                    >
                        {isKnowGuest ? 'Confirmar' : 'Cadastrar'}
                    </Button>
                </Flex>
            </Form>
        </Flex>
    );
}
